import { useApolloClient, useMutation } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'

import { Button, Fields, Link, Row, Stack, useAlert } from '..'
import { mutations, queries } from '../../graphql'
import { useAuth } from '../../hooks'
import { validations } from '../form'
import { notifyBrandingUpdated } from '../../utils/brandingEvents'

export function LoginForm({ prefillEmail, onCompleted, close }) {
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [apiDown, setApiDown] = useState(false)
  const [, { login }] = useAuth()
  const { register, handleSubmit, formState } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { email: prefillEmail },
  })
  const { errors = {} } = formState
  const client = useApolloClient()

  const Retry = async () => {
    setAlert({
      message: 'Retrying...',
      severity: 'error',
    })
    // eslint-disable-next-line no-use-before-define
    await checkApiHealth()
  }

  function serviceDownSetAlert() {
    setAlert({
      message: (
        <>
          We&apos;re currently experiencing a temporary outage. Our team is working on it — please check back shortly.
          <br />
          <br />
          To report this issue, please contact us at <Link href="mailto:support@givewise.ca">support@givewise.ca</Link>
        </>
      ),
      severity: 'error',
      timeout: 60000,
      action: {
        label: 'Retry',
        onClick: Retry,
        sx: { mt: 3 },
      },
    })
  }

  const checkApiHealth = async () => {
    try {
      const apiHost = process.env.REACT_APP_API_HOST

      if (!apiHost) {
        console.error('REACT_APP_API_HOST environment variable is not set')
        return
      }
      const response = await fetch(`${apiHost}/health/api`)
      const healthData = await response.json()

      if (healthData.status === 'down') {
        serviceDownSetAlert()
        setApiDown(true)
      } else {
        setApiDown(false)
      }
    } catch (error) {
      serviceDownSetAlert()
      setApiDown(true)
    }
  }

  useEffect(() => {
    checkApiHealth()
  }, [])

  const [loginMutation, { loading }] = useMutation(mutations.auth.login, {
    onCompleted: async ({ login: { access_token: token } = {} }) => {
      await login(token)
      await client.refetchQueries({ include: [{ query: queries.user.me }] })

      // Re-fetch branding now that user is logged in (may have user-specific branding)
      notifyBrandingUpdated()

      onCompleted?.()
      close?.()
    },
    onError: (e) => {
      if (e.networkError?.result?.message === 'LOGIN_FAILED_USER_IS_GUEST') {
        setAlert({
          message: `This email may have been used for Guest Giving and is not an Active Fund. Click “Sign\u00A0Up” to register account or email support@givewise.ca for assistance.`,
          severity: 'info',
          timeout: 16000,
        })
      } else if (e.networkError?.result?.message === 'LOGIN_FAILED_USER_UNCONFIRMED') {
        setAlert({
          message:
            'Your account has not yet been confirmed. Please check your email for the link to confirm your account, or contact support@givewise.ca for assistance.',
          severity: 'error',
        })
      } else if (e.networkError?.result?.message === 'LOGIN_FAILED_USER_BLOCKED') {
        setAlert({
          message: 'This Giving Fund has been deleted.',
          severity: 'error',
        })
      } else if (e.networkError?.result?.message === 'LOGIN_FAILED_USER_CLOSED') {
        setAlert({
          message: 'This account has been closed. Please contact support@givewise.ca for assistance.',
          severity: 'error',
        })
      } else if (e.networkError?.result?.message === 'LOGIN_FAILED_BAD_CREDENTIALS') {
        setAlert({ message: 'Username or password is incorrect.', severity: 'error' })
      } else if (apiDown) {
        serviceDownSetAlert()
      } else {
        setAlert({ message: 'An unexpected error has occurred', severity: 'error' })
      }
    },
  })

  const onSubmit = ({ email, password }) => {
    loginMutation({ variables: { email, password } })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4} alignItems="flex-end">
        <Fields.Text
          data-testid="email"
          type="email"
          autoComplete="username"
          label="Email *"
          error={!!errors.email}
          helperText={errors.email?.message}
          {...register('email', { ...validations.required, ...validations.email })}
        />
        <Fields.Password autoComplete="current-password" register={register} errors={errors} />
        <Row justifyContent="flex-end" alignItems="center" spacing={4}>
          <Link to="/forgot-password" data-testid="forgPasswordLink">
            Forgot password?
          </Link>
          <Button type="submit" disabled={loading} data-testid="signInButton">
            Sign In
          </Button>
        </Row>
        <Alert {...alertProps} />
      </Stack>
    </form>
  )
}
